import html2canvas from "html2canvas";
import {
    PDF_CONTENT_MARGINS,
    PdfPageLayoutOptions,
    getPdfPageHeightMm,
    getPdfPageWidthMm,
} from "./pdf-content.config";

declare const window: Window & {
  jspdf: { jsPDF: new (opts: Record<string, unknown>) => JsPdfDoc };
};

export type GenerateHtmlElementPdfOptions = PdfPageLayoutOptions;

const HTML2CANVAS_SCALE = 2;

/** Max element height (px, unscaled) per html2canvas capture to avoid canvas limits. */
const MAX_CAPTURE_HEIGHT_PX = 2800;

/** Row-aware page breaks are skipped above this count for performance. */
const MAX_ROWS_FOR_KEEP_TOGETHER = 200;

const JPEG_QUALITY = 0.92;

/** Minimum slice height (canvas px) before forcing a row onto the next page. */
const MIN_SLICE_PX = 24;

interface KeepTogetherZone {
  top: number;
  bottom: number;
}

interface JsPdfDoc {
  addPage: () => void;
  addImage: (
    imageData: string,
    format: string,
    x: number,
    y: number,
    width: number,
    height: number
  ) => void;
  output: (type: string) => string;
}

function shouldUseKeepTogether(element: HTMLElement): boolean {
  return element.querySelectorAll("tr").length <= MAX_ROWS_FOR_KEEP_TOGETHER;
}

/**
 * Collects vertical ranges of table rows and other elements that must not be
 * split across PDF pages. Coordinates are in canvas pixels (scaled).
 */
function collectKeepTogetherZones(
  root: HTMLElement,
  scale: number,
  offsetY = 0,
  chunkHeight?: number
): KeepTogetherZone[] {
  const rootTop = root.getBoundingClientRect().top;
  const zones: KeepTogetherZone[] = [];
  const chunkBottom =
    chunkHeight !== undefined
      ? offsetY + chunkHeight
      : Number.POSITIVE_INFINITY;

  root.querySelectorAll("tr, [data-pdf-keep-together]").forEach((node) => {
    const rect = node.getBoundingClientRect();
    const top = rect.top - rootTop;
    const bottom = rect.bottom - rootTop;

    if (bottom <= offsetY || top >= chunkBottom) {
      return;
    }

    zones.push({
      top: Math.floor((top - offsetY) * scale),
      bottom: Math.ceil((bottom - offsetY) * scale),
    });
  });

  return zones.sort((a, b) => a.top - b.top);
}

/**
 * Adjusts a proposed page-break position so it does not cut through a
 * keep-together zone (e.g. a table row).
 */
function adjustSliceEnd(
  startPx: number,
  proposedEndPx: number,
  maxPx: number,
  zones: KeepTogetherZone[]
): number {
  let endPx = Math.min(Math.floor(proposedEndPx), maxPx);

  for (const zone of zones) {
    const cutsThroughRow = endPx > zone.top && endPx < zone.bottom;
    if (!cutsThroughRow) continue;

    if (zone.top > startPx + MIN_SLICE_PX) {
      endPx = zone.top;
    }
    break;
  }

  for (const zone of zones) {
    if (zone.top <= startPx || zone.top >= endPx) continue;

    const spaceBeforeRow = zone.top - startPx;
    const rowHeight = zone.bottom - zone.top;
    const sliceHeight = endPx - startPx;

    if (spaceBeforeRow < MIN_SLICE_PX && rowHeight <= sliceHeight) {
      endPx = zone.top;
      break;
    }
  }

  return Math.max(endPx, Math.min(startPx + 1, maxPx));
}

async function captureElementChunk(
  element: HTMLElement,
  offsetY: number,
  height: number
): Promise<HTMLCanvasElement> {
  return html2canvas(element, {
    scale: HTML2CANVAS_SCALE,
    useCORS: true,
    scrollX: 0,
    scrollY: -offsetY,
    y: offsetY,
    height,
    backgroundColor: "#ffffff",
    logging: false,
  });
}

function appendCanvasToPdf(
  pdf: JsPdfDoc,
  canvas: HTMLCanvasElement,
  options: GenerateHtmlElementPdfOptions | undefined,
  keepTogetherZones: KeepTogetherZone[],
  pageIndex: { value: number }
): void {
  const margins = options?.margins ?? PDF_CONTENT_MARGINS;
  const { top, bottom, left, right } = margins;
  const pageWidth = getPdfPageWidthMm(options);
  const pageHeight = getPdfPageHeightMm(options);
  const usableWidth = pageWidth - left - right;
  const usableHeightMm = pageHeight - top - bottom;

  const imgWidthMm = usableWidth;
  const imgHeightMm = (canvas.height / canvas.width) * imgWidthMm;
  const usableHeightPx = Math.floor(
    (usableHeightMm / imgHeightMm) * canvas.height
  );

  let startPx = 0;

  while (startPx < canvas.height) {
    if (pageIndex.value > 0) {
      pdf.addPage();
    }

    const proposedEndPx = Math.min(startPx + usableHeightPx, canvas.height);
    const endPx = adjustSliceEnd(
      startPx,
      proposedEndPx,
      canvas.height,
      keepTogetherZones
    );
    const sliceHeightPx = endPx - startPx;

    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeightPx;
    const ctx = sliceCanvas.getContext("2d");

    if (ctx) {
      ctx.drawImage(
        canvas,
        0,
        startPx,
        canvas.width,
        sliceHeightPx,
        0,
        0,
        canvas.width,
        sliceHeightPx
      );
    }

    const sliceHeightMm = (sliceHeightPx / canvas.height) * imgHeightMm;
    const sliceData = sliceCanvas.toDataURL("image/jpeg", JPEG_QUALITY);
    pdf.addImage(sliceData, "JPEG", left, top, imgWidthMm, sliceHeightMm);

    startPx = endPx;
    pageIndex.value += 1;
  }
}

/**
 * Renders a DOM element to a multi-page A4 PDF data URI (html2canvas + jsPDF).
 * Tall content is captured in vertical chunks to stay within browser canvas limits.
 */
export async function generateHtmlElementPdf(
  element: HTMLElement,
  options?: GenerateHtmlElementPdfOptions,
  onCaptureProgress?: (current: number, total: number) => void
): Promise<string> {
  const orientation = options?.orientation ?? "portrait";
  const pdf = new window.jspdf.jsPDF({
    unit: "mm",
    format: "a4",
    orientation,
  });

  const pageIndex = { value: 0 };
  const totalHeight = element.scrollHeight;
  const useKeepTogether = shouldUseKeepTogether(element);
  const chunkCount = Math.max(
    1,
    Math.ceil(totalHeight / MAX_CAPTURE_HEIGHT_PX)
  );

  let offsetY = 0;
  let chunkIndex = 0;

  while (offsetY < totalHeight) {
    const chunkHeight = Math.min(MAX_CAPTURE_HEIGHT_PX, totalHeight - offsetY);
    const canvas = await captureElementChunk(element, offsetY, chunkHeight);
    const keepTogetherZones = useKeepTogether
      ? collectKeepTogetherZones(
          element,
          HTML2CANVAS_SCALE,
          offsetY,
          chunkHeight
        )
      : [];

    appendCanvasToPdf(pdf, canvas, options, keepTogetherZones, pageIndex);

    offsetY += chunkHeight;
    chunkIndex += 1;
    onCaptureProgress?.(chunkIndex, chunkCount);

    await new Promise((resolve) => {
      setTimeout(resolve, 0);
    });
  }

  const output = pdf.output("datauristring");

  if (!output.startsWith("data:application/pdf")) {
    throw new Error("Không tạo được file PDF");
  }

  return output;
}
